import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { LOCALES, SUPPORTED_LOCALES } from './locales';

const STORAGE_KEY = 'peipei.locale';
const LocaleContext = createContext(null);

// Normalize any browser language tag to one of our supported locales.
function normalizeTag(tag) {
  if (!tag) return null;
  const lower = tag.toLowerCase();
  if (lower.startsWith('en')) return 'en';
  if (lower === 'zh-hk' || lower === 'zh-mo' || lower === 'zh-tw' || lower.includes('hant')) return 'zh-HK';
  if (lower.startsWith('zh')) return 'zh-CN';
  return null;
}

function getSavedLocale() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
  } catch (e) {
    // storage unavailable, ignore
  }
  return null;
}

function getBrowserLocale() {
  const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (const tag of langs) {
    const match = normalizeTag(tag);
    if (match) return match;
  }
  return null;
}

// Priority: saved choice > browser language > Cloudflare country hint > English.
function detectInitialLocale() {
  return getSavedLocale() || getBrowserLocale() || 'en';
}

// Cloudflare regional default. Only applied when the user has not saved a
// choice and the browser did not request a supported language explicitly.
async function fetchCountryDefault() {
  try {
    const res = await fetch('/cdn-cgi/trace', { signal: AbortSignal.timeout(2500) });
    if (!res.ok) return null;
    const text = await res.text();
    const loc = (text.match(/^loc=(\w+)/m) || [])[1];
    if (loc === 'CN') return 'zh-CN';
    if (loc === 'HK' || loc === 'MO') return 'zh-HK';
    return null;
  } catch (e) {
    return null;
  }
}

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(detectInitialLocale);

  // Cloudflare country routing as third priority.
  useEffect(() => {
    if (getSavedLocale() || getBrowserLocale()) return;
    let cancelled = false;
    fetchCountryDefault().then((countryLocale) => {
      if (countryLocale && !cancelled) setLocaleState(countryLocale);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Reflect the locale in document metadata.
  useEffect(() => {
    const t = LOCALES[locale];
    document.documentElement.lang = locale;
    document.title = t.meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t.meta.description);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', t.meta.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', t.meta.description);
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // storage unavailable, ignore
    }
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale, t: LOCALES[locale], locales: LOCALES, supported: SUPPORTED_LOCALES }),
    [locale, setLocale]
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider');
  return ctx;
};
